import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate } from "react-router-dom";
import { landingAssetsApi } from "../../api/landingAssets";
import { settingsApi } from "../../api/settings";
import PendingAssetField from "../../components/dashboard/landingEditor/PendingAssetField";
import ProjectDeploymentCard from "../../components/dashboard/ProjectDeploymentCard";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { assetValueFromResponse } from "../../utils/landingAssets";
import { isSuperAdmin } from "../../utils/roles";
import {
  PROJECT_BUSINESS_PROFILES,
  businessGoalsForProfile,
  capabilitiesForBusinessGoals,
} from "../../utils/projectCapabilities";
import {
  activeModuleLabels,
  BILLING_PLAN_LABELS,
  modulesForPlan,
  normalizeProjectModules,
  normalizeProjectPlanCode,
} from "../../utils/projectModules";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  purple: "#6957E8",
  yellow: "#FFC857",
  green: "#26A269",
  blue: "#2F80ED",
};
const emptyBranding = { logo: null, compactLogo: null, favicon: null };

function emptyForm(email = "", plan = "catalogo") {
  return {
    name: "",
    slug: "",
    ownerEmail: email,
    plan,
    businessProfile: "",
    businessGoals: [],
    domains: "",
  };
}

function makeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function uploadInitialBranding(projectId, branding, businessName) {
  const uploads = [
    {
      file: branding.logo,
      field: "logoUrl",
      sectionKey: "branding",
      assetKey: "logo",
    },
    {
      file: branding.compactLogo,
      field: "compactLogoUrl",
      sectionKey: "branding",
      assetKey: "compact-logo",
    },
    {
      file: branding.favicon,
      field: "faviconUrl",
      sectionKey: "branding",
      assetKey: "favicon",
    },
  ].filter((item) => item.file);

  if (!uploads.length) return;

  const uploadedPaths = [];
  const theme = {};

  try {
    for (const item of uploads) {
      const response = await landingAssetsApi.upload(projectId, item.file, {
        pageKey: "home",
        sectionKey: item.sectionKey,
        assetKey: item.assetKey,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El backend no devolvió la ruta del archivo");
      uploadedPaths.push(value);
      theme[item.field] = value;
    }

    await settingsApi.update(projectId, {
      business: { name: businessName },
      seo: { title: businessName },
      theme,
    });
  } catch (error) {
    await Promise.all(
      uploadedPaths.map((path) => landingAssetsApi.remove(projectId, path).catch(() => null))
    );
    throw error;
  }
}

function BusinessGoalsSelector({ businessProfile, value, onChange, compact = false }) {
  const goals = businessGoalsForProfile(businessProfile);
  if (!goals.length) return null;

  const selected = new Set(Array.isArray(value) ? value : []);
  const toggleGoal = (goalKey) => {
    const next = selected.has(goalKey)
      ? [...selected].filter((key) => key !== goalKey)
      : [...selected, goalKey];
    onChange(next);
  };

  return (
    <Box
      sx={{
        p: compact ? 1.05 : 1.6,
        borderRadius: compact ? 2.15 : 3,
        bgcolor: "rgba(105,87,232,.045)",
        border: "1px solid rgba(105,87,232,.16)",
      }}
    >
      <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: compact ? ".78rem" : ".92rem" }}>
        ¿Qué querés hacer con tu negocio?
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: .25, color: "rgba(25,36,47,.56)", lineHeight: 1.4 }}
      >
        Elegí sólo lo que necesitás ahora. Podés seguir sin marcar nada y cambiarlo después desde Configuración.
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={compact ? .6 : .8} sx={{ mt: compact ? .8 : 1.1 }}>
        {goals.map((goal) => {
          const active = selected.has(goal.key);
          return (
            <Chip
              key={goal.key}
              label={goal.label}
              clickable
              onClick={() => toggleGoal(goal.key)}
              variant={active ? "filled" : "outlined"}
              size={compact ? "small" : "medium"}
              sx={{
                fontWeight: 850,
                bgcolor: active ? "rgba(105,87,232,.13)" : "#fff",
                color: active ? BRAND.purple : BRAND.dark,
                borderColor: active ? "rgba(105,87,232,.42)" : "rgba(25,36,47,.15)",
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

function FirstProjectOnboarding({
  form,
  creating,
  createStatus,
  error,
  onChange,
  onGoalsChange,
  onSubmit,
}) {
  const isMobile = useMediaQuery("(max-width:599.95px)");
  const generatedSlug = makeSlug(form.slug || form.name);
  const businessReady = Boolean(form.name.trim() && generatedSlug);
  const addressPreview = generatedSlug || "se genera desde el nombre";

  if (isMobile) {
    return (
      <Box component="form" onSubmit={onSubmit} sx={{ pb: 8.5 }}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            p: 1.45,
            borderRadius: 2.75,
            border: "1px solid rgba(25,36,47,.08)",
            bgcolor: "#fff",
            boxShadow: "0 10px 28px rgba(25,36,47,.08)",
          }}
        >
          <Box sx={{ position: "absolute", width: 118, height: 118, borderRadius: "50%", bgcolor: "rgba(105,87,232,.08)", right: -54, top: -64 }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ position: "relative" }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2.2,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: "rgba(243,90,58,.1)",
                color: BRAND.orange,
              }}
            >
              <StorefrontRoundedIcon sx={{ fontSize: 21 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ color: BRAND.orange, fontSize: ".62rem", fontWeight: 1000, letterSpacing: .45 }}>
                PRIMER PROYECTO
              </Typography>
              <Typography sx={{ mt: .05, color: BRAND.dark, fontSize: "1.28rem", lineHeight: 1.08, fontWeight: 1000, letterSpacing: -.35 }}>
                Empezá con lo esencial
              </Typography>
            </Box>
          </Stack>

          <Typography sx={{ position: "relative", mt: .7, color: "rgba(25,36,47,.58)", fontSize: ".77rem", lineHeight: 1.38 }}>
            Poné el nombre de tu negocio. El rubro y los objetivos son opcionales y sólo sirven para ordenar las primeras sugerencias.
          </Typography>

          {error ? <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: .15, "& .MuiAlert-message": { fontSize: ".76rem" } }}>{error}</Alert> : null}

          <Stack spacing={1.05} sx={{ mt: 1.25, position: "relative" }}>
            <TextField
              label="Nombre del negocio"
              name="name"
              value={form.name}
              onChange={onChange}
              required
              fullWidth
              size="small"
              placeholder="Ej: Repuestos RR"
              autoComplete="organization"
              InputProps={{ sx: { borderRadius: 2.15, fontSize: ".9rem" } }}
              InputLabelProps={{ sx: { fontSize: ".86rem" } }}
            />
            <TextField
              select
              label="Tipo de negocio (opcional)"
              name="businessProfile"
              value={form.businessProfile}
              onChange={onChange}
              fullWidth
              size="small"
              helperText="No limita funciones y podés cambiarlo después."
              InputProps={{ sx: { borderRadius: 2.15, fontSize: ".9rem" } }}
              InputLabelProps={{ sx: { fontSize: ".86rem" } }}
            >
              <MenuItem value="">Elegir después</MenuItem>
              {PROJECT_BUSINESS_PROFILES.map((profile) => (
                <MenuItem key={profile.key} value={profile.key}>{profile.label}</MenuItem>
              ))}
            </TextField>
            <BusinessGoalsSelector
              compact
              businessProfile={form.businessProfile}
              value={form.businessGoals}
              onChange={onGoalsChange}
            />
          </Stack>

          <Box
            component="details"
            sx={{
              mt: 1,
              borderTop: "1px solid rgba(25,36,47,.07)",
              "&[open] summary": { mb: .9 },
            }}
          >
            <Box
              component="summary"
              sx={{
                pt: 1,
                cursor: "pointer",
                color: "rgba(25,36,47,.62)",
                fontWeight: 850,
                fontSize: ".74rem",
                "&::marker": { color: BRAND.purple },
              }}
            >
              Cambiar dirección inicial de la web
            </Box>
            <TextField
              label="Dirección inicial"
              name="slug"
              value={form.slug}
              onChange={onChange}
              fullWidth
              size="small"
              placeholder={makeSlug(form.name) || "mi-negocio"}
              helperText={`Si no la tocás, usamos automáticamente: ${addressPreview}`}
              autoComplete="off"
              InputProps={{ sx: { borderRadius: 2.15, fontSize: ".9rem" } }}
              InputLabelProps={{ sx: { fontSize: ".86rem" } }}
            />
          </Box>

          <Paper elevation={0} sx={{ mt: 1.2, p: 1.05, borderRadius: 2.1, bgcolor: "rgba(38,162,105,.055)", border: "1px solid rgba(38,162,105,.14)" }}>
            <Stack direction="row" spacing={.8} alignItems="flex-start">
              <CheckCircleRoundedIcon sx={{ color: BRAND.green, fontSize: 17, mt: .05 }} />
              <Typography sx={{ color: "rgba(25,36,47,.62)", fontSize: ".73rem", lineHeight: 1.4 }}>
                Logos, dominio propio, contenidos, cobros y configuraciones avanzadas se completan después desde el panel cuando hagan falta.
              </Typography>
            </Stack>
          </Paper>
        </Paper>

        {createStatus ? (
          <Alert severity="info" sx={{ mt: 1, borderRadius: 2, py: .15, "& .MuiAlert-message": { fontSize: ".76rem" } }}>
            {createStatus}
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: "max(10px, env(safe-area-inset-bottom))",
            zIndex: 1200,
            p: .7,
            borderRadius: 2.6,
            bgcolor: "rgba(255,255,255,.96)",
            border: "1px solid rgba(25,36,47,.09)",
            boxShadow: "0 12px 32px rgba(25,36,47,.16)",
            backdropFilter: "blur(12px)",
          }}
        >
          <Button
            type="submit"
            fullWidth
            disabled={creating || !businessReady}
            variant="contained"
            endIcon={!creating ? <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} /> : null}
            sx={{
              minHeight: 41,
              bgcolor: BRAND.orange,
              borderRadius: 2.05,
              textTransform: "none",
              fontSize: ".86rem",
              fontWeight: 1000,
              boxShadow: "none",
              "&:hover": { bgcolor: "#dd472b", boxShadow: "none" },
            }}
          >
            {creating ? "Creando tu proyecto..." : "Crear mi proyecto"}
          </Button>
          {!businessReady ? (
            <Typography variant="caption" sx={{ display: "block", mt: .35, color: "rgba(25,36,47,.48)", textAlign: "center", fontSize: ".65rem", lineHeight: 1.2 }}>
              Escribí el nombre de tu negocio para continuar.
            </Typography>
          ) : null}
        </Paper>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: 4, md: 6 },
          p: { xs: 3, sm: 4, md: 5 },
          background: "linear-gradient(120deg, #FFF3E8 0%, #FFF9F4 48%, #F1EEFF 100%)",
          border: "1px solid rgba(243,90,58,.12)",
        }}
      >
        <Box sx={{ position: "absolute", width: 240, height: 240, borderRadius: "50%", bgcolor: "rgba(105,87,232,.08)", right: -80, top: -110 }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ position: "relative" }}>
          <Box sx={{ maxWidth: 760 }}>
            <Chip icon={<AutoAwesomeRoundedIcon />} label="PRIMER PROYECTO" sx={{ bgcolor: "#fff", color: BRAND.orange, fontWeight: 1000, boxShadow: "0 8px 24px rgba(25,36,47,.07)", "& .MuiChip-icon": { color: BRAND.yellow } }} />
            <Typography variant="h2" sx={{ mt: 2, color: BRAND.dark, fontSize: { xs: "2.15rem", md: "3.35rem" }, lineHeight: 1.02, fontWeight: 1000, letterSpacing: -1.8 }}>
              Empezá sin configurar todo
            </Typography>
            <Typography sx={{ mt: 1.5, color: "rgba(25,36,47,.65)", fontSize: { xs: "1rem", md: "1.08rem" }, lineHeight: 1.65, maxWidth: 680 }}>
              Para arrancar sólo necesitamos el nombre de tu negocio. Si querés, indicá el rubro y qué querés hacer primero para ordenar mejor las sugerencias; ninguna función queda bloqueada por esas elecciones.
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ maxWidth: 360, p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,.72)", border: "1px solid rgba(25,36,47,.08)" }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 1000 }}>Lo demás puede esperar</Typography>
            <Typography variant="caption" sx={{ display: "block", mt: .45, color: "rgba(25,36,47,.58)", lineHeight: 1.45 }}>
              Logos, dominio propio, contenidos, cobros y ajustes avanzados se completan desde el panel cuando realmente los necesites.
            </Typography>
          </Paper>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}

      <Box component="form" onSubmit={onSubmit}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={7}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 5, border: "1px solid rgba(243,90,58,.18)", boxShadow: "0 18px 50px rgba(25,36,47,.06)" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 3.2, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.11)", color: BRAND.orange }}>
                  <StorefrontRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 1000, letterSpacing: .8 }}>DATOS INICIALES</Typography>
                  <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 1000 }}>Contanos lo mínimo para empezar</Typography>
                </Box>
              </Stack>

              <Stack spacing={2.2}>
                <TextField
                  label="Nombre del negocio"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  autoFocus
                  fullWidth
                  placeholder="Ej: Repuestos RR"
                  helperText="Es el único dato obligatorio de este paso."
                  InputProps={{ sx: { borderRadius: 3 } }}
                />
                <TextField
                  select
                  label="Tipo de negocio (opcional)"
                  name="businessProfile"
                  value={form.businessProfile}
                  onChange={onChange}
                  fullWidth
                  helperText="Sólo ordena sugerencias iniciales. No limita módulos ni funciones y podés cambiarlo después."
                  InputProps={{ sx: { borderRadius: 3 } }}
                >
                  <MenuItem value="">Elegir después</MenuItem>
                  {PROJECT_BUSINESS_PROFILES.map((profile) => (
                    <MenuItem key={profile.key} value={profile.key}>{profile.label}</MenuItem>
                  ))}
                </TextField>
                <BusinessGoalsSelector
                  businessProfile={form.businessProfile}
                  value={form.businessGoals}
                  onChange={onGoalsChange}
                />

                <Box component="details" sx={{ borderTop: "1px solid rgba(25,36,47,.08)", pt: 1.2, "&[open] summary": { mb: 1.5 } }}>
                  <Box component="summary" sx={{ cursor: "pointer", color: "rgba(25,36,47,.62)", fontWeight: 900, "&::marker": { color: BRAND.purple } }}>
                    Opciones avanzadas de la dirección
                  </Box>
                  <Stack spacing={1.3}>
                    <TextField
                      label="Dirección inicial de la web"
                      name="slug"
                      value={form.slug}
                      onChange={onChange}
                      fullWidth
                      placeholder={makeSlug(form.name) || "mi-negocio"}
                      helperText="La generamos automáticamente desde el nombre. Cambiala sólo si necesitás otra."
                      InputProps={{ sx: { borderRadius: 3 } }}
                    />
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ px: 1.5, py: 1.1, borderRadius: 3, bgcolor: "rgba(47,128,237,.045)" }}>
                      <LanguageRoundedIcon sx={{ color: BRAND.blue, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
                        Identificador inicial: <Box component="span" sx={{ color: BRAND.dark, fontWeight: 900 }}>{addressPreview}</Box>
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 5, bgcolor: BRAND.dark, color: "#fff", position: { lg: "sticky" }, top: { lg: 24 }, overflow: "hidden", boxShadow: "0 24px 65px rgba(25,36,47,.18)" }}>
              <Box sx={{ position: "absolute", width: 190, height: 190, borderRadius: "50%", bgcolor: "rgba(105,87,232,.18)", right: -80, top: -75 }} />
              <Box sx={{ position: "relative" }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 48, height: 48, borderRadius: 3.2, display: "grid", placeItems: "center", bgcolor: "rgba(38,162,105,.16)", color: "#65D69B" }}>
                    <RocketLaunchRoundedIcon />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#65D69B", fontWeight: 1000, letterSpacing: .8 }}>DESPUÉS DE CREARLO</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 1000 }}>Seguís desde tu panel</Typography>
                  </Box>
                </Stack>

                <Stack spacing={1.45} sx={{ mt: 3 }}>
                  {[
                    "La dirección inicial se genera automáticamente.",
                    "Tus elecciones sólo priorizan las herramientas para empezar.",
                    "Las demás capacidades siguen disponibles y se configuran cuando las necesites.",
                  ].map((text) => (
                    <Stack key={text} direction="row" spacing={1} alignItems="flex-start">
                      <CheckCircleRoundedIcon sx={{ color: "#65D69B", fontSize: 20, mt: .1 }} />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,.78)", lineHeight: 1.55 }}>{text}</Typography>
                    </Stack>
                  ))}
                </Stack>

                {createStatus ? (
                  <Alert severity="info" sx={{ mt: 3, mb: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,.92)" }}>
                    {createStatus}
                  </Alert>
                ) : null}

                <Button
                  type="submit"
                  fullWidth
                  disabled={creating || !businessReady}
                  size="large"
                  variant="contained"
                  endIcon={!creating ? <ArrowForwardRoundedIcon /> : null}
                  sx={{
                    mt: 3,
                    bgcolor: BRAND.orange,
                    borderRadius: 999,
                    py: 1.55,
                    px: 2.5,
                    textTransform: "none",
                    fontWeight: 1000,
                    fontSize: "1rem",
                    boxShadow: "0 16px 34px rgba(243,90,58,.32)",
                    "&:hover": { bgcolor: "#dd472b", transform: "translateY(-2px)" },
                    "&.Mui-disabled": { bgcolor: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.38)" },
                    transition: "all .2s ease",
                  }}
                >
                  {creating ? "Creando tu proyecto..." : "Crear mi proyecto"}
                </Button>
                {!businessReady ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 1.2, textAlign: "center", color: "rgba(255,255,255,.52)" }}>
                    Escribí el nombre de tu negocio para continuar.
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ display: "block", mt: 1.2, textAlign: "center", color: "rgba(255,255,255,.52)" }}>
                    No necesitás completar ninguna configuración avanzada ahora.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const {
    projects,
    activeProject,
    projectCreation,
    setActiveProject,
    createProject,
    loadProjects,
    loading,
    error,
  } = useProjects();
  const [form, setForm] = React.useState(() => emptyForm(
    user?.email || "",
    "plus",
  ));
  const [branding, setBranding] = React.useState(emptyBranding);
  const [localError, setLocalError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createStatus, setCreateStatus] = React.useState("");

  const ownedCount = Number(projectCreation?.ownedCount ?? (superAdmin ? 0 : projects.length));
  const canCreateProject = superAdmin || Boolean(
    projectCreation?.canCreate ?? (!superAdmin && projects.length === 0),
  );
  const showCreatePanel = superAdmin || canCreateProject;
  const creationReason = String(projectCreation?.reason || "");
  const subscriptionTarget = projectCreation?.subscriptionProject || activeProject || projects[0] || null;
  const firstProjectMode = !superAdmin && projects.length === 0 && canCreateProject;

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    setForm((previous) => ({
      ...previous,
      ownerEmail: previous.ownerEmail || user?.email || "",
    }));
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => {
      const next = { ...previous, [name]: value };
      if (name === "name") next.slug = makeSlug(value);
      if (name === "businessProfile" && value !== previous.businessProfile) next.businessGoals = [];
      return next;
    });
  };

  const handleGoalsChange = (businessGoals) => {
    setForm((previous) => ({ ...previous, businessGoals }));
  };

  const handleBrandingChange = (key, file) => {
    setBranding((previous) => ({ ...previous, [key]: file }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let project = null;

    try {
      setCreating(true);
      setCreateStatus(firstProjectMode ? "Creando tu proyecto..." : "Creando el proyecto y su espacio privado...");
      setLocalError("");

      const createPayload = {
        name: form.name,
        slug: makeSlug(form.slug || form.name),
        ownerEmail: form.ownerEmail,
        plan: form.plan,
        businessProfile: form.businessProfile,
        domains: form.domains,
        modules: modulesForPlan(form.plan),
      };

      if (firstProjectMode) {
        createPayload.businessGoals = form.businessGoals;
        createPayload.capabilities = capabilitiesForBusinessGoals(
          form.businessProfile,
          form.businessGoals,
        );
      }

      project = await createProject(createPayload);

      setCreateStatus(firstProjectMode ? "Proyecto creado. Estamos preparando tu espacio..." : "Proyecto creado. Render está preparando la página pública...");

      const hasBranding = Boolean(branding.logo || branding.compactLogo || branding.favicon);
      if (hasBranding && project?.slug) {
        setCreateStatus("Subiendo tus logos y recursos visuales...");
        await uploadInitialBranding(project.slug, branding, form.name);
      }

      setForm(emptyForm(user?.email || "", "plus"));
      setBranding(emptyBranding);

      if (project?.slug) {
        navigate(firstProjectMode ? "/dashboard" : `/dashboard/proyectos/${project.slug}/pagina`);
      }
    } catch (err) {
      if (project?.slug) {
        setLocalError(
          `El proyecto ${project.name || project.slug} fue creado, pero no se pudo completar la identidad visual: ${err.message || "error desconocido"}. Podés reintentar desde Landing > Ajustes > Recursos visuales.`
        );
        await loadProjects();
      } else {
        setLocalError(err.message || "No se pudo crear el proyecto");
      }
    } finally {
      setCreating(false);
      setCreateStatus("");
    }
  };

  if (firstProjectMode) {
    return (
      <FirstProjectOnboarding
        form={form}
        creating={creating}
        createStatus={createStatus}
        error={localError || error}
        onChange={handleChange}
        onGoalsChange={handleGoalsChange}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
          Proyectos
        </Typography>
        <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
          {superAdmin
            ? "Acá das de alta clientes. Cada proyecto apunta a una DB y un espacio de archivos propios."
            : "Administrá tus proyectos y revisá cuándo podés sumar uno nuevo."}
        </Typography>
      </Box>

      {(error || localError) && <Alert severity="error" onClose={() => setLocalError("")}>{localError || error}</Alert>}

      {!superAdmin && !canCreateProject && creationReason === "subscription_required" ? (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(243,90,58,.22)", bgcolor: "rgba(243,90,58,.045)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 42, height: 42, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.12)", color: BRAND.orange, flexShrink: 0 }}>
                <WorkspacePremiumRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Tu primer proyecto ya está creado
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.66)", maxWidth: 720 }}>
                  Para agregar un segundo proyecto necesitás activar una suscripción en tu proyecto actual. La suscripción habilita un proyecto adicional sin modificar el que ya tenés.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              disabled={!subscriptionTarget?.slug}
              onClick={() => subscriptionTarget?.slug && navigate(`/dashboard/proyectos/${subscriptionTarget.slug}/configuracion/suscripcion`)}
              sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, px: 2.5, flexShrink: 0 }}
            >
              Activar suscripción
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {!superAdmin && !canCreateProject && creationReason === "limit_reached" ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Tu cuenta ya utiliza los {projectCreation?.maxProjects || 2} proyectos disponibles: el primero incluido y uno adicional habilitado por la suscripción.
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        {showCreatePanel && (
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                  <AddRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                    {superAdmin ? "Nuevo cliente" : "Agregar otro proyecto"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                    {superAdmin
                      ? "Proyecto, dominio e identidad inicial en un solo flujo."
                      : "Tu suscripción habilitó un proyecto adicional para esta cuenta."}
                  </Typography>
                </Box>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField label="Nombre del negocio" name="name" value={form.name} onChange={handleChange} required fullWidth />
                  <TextField label="Slug" name="slug" value={form.slug} onChange={handleChange} required fullWidth helperText="Ej: repuestos-rr → DB: wp_repuestos_rr" />
                  <TextField
                    select
                    label="Tipo de negocio (opcional)"
                    name="businessProfile"
                    value={form.businessProfile}
                    onChange={handleChange}
                    helperText="Sugiere capacidades iniciales; luego se pueden activar o desactivar una por una."
                    fullWidth
                  >
                    <MenuItem value="">Configurar después</MenuItem>
                    {PROJECT_BUSINESS_PROFILES.map((profile) => (
                      <MenuItem key={profile.key} value={profile.key}>{profile.label}</MenuItem>
                    ))}
                  </TextField>
                  {superAdmin ? (
                    <TextField label="Email owner" name="ownerEmail" type="email" value={form.ownerEmail} onChange={handleChange} required fullWidth />
                  ) : null}
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(38,162,105,.045)", borderColor: "rgba(38,162,105,.22)" }}>
                    <Typography variant="caption" sx={{ color: BRAND.green, fontWeight: 1000 }}>
                      PRUEBA INICIAL AUTOMÁTICA
                    </Typography>
                    <Typography sx={{ mt: 0.35, fontWeight: 950, color: BRAND.dark }}>
                      Sistema completo durante 30 días
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25, color: "rgba(25,36,47,.62)" }}>
                      El proyecto nace en trial con landing, catálogo, pedidos, cobros, sistema interno, estadísticas y Web Push habilitados. Al finalizar, el cliente elige qué servicios conservar.
                    </Typography>
                  </Paper>
                  <TextField
                    label="Dominio público"
                    name="domains"
                    value={form.domains}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    helperText="Opcional. Uno por línea o separados por coma. Podés pegar https://cliente.com y el backend guardará sólo el hostname."
                  />

                  <Divider />
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Identidad inicial</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(25,36,47,.60)", mt: 0.25 }}>
                      Opcional. Después se puede reemplazar desde el editor visual sin tocar código.
                    </Typography>
                  </Box>
                  <PendingAssetField
                    label="Logo principal"
                    file={branding.logo}
                    disabled={creating}
                    helperText="SVG, PNG, WebP o JPG. Se guarda en el espacio del cliente."
                    onChange={(file) => handleBrandingChange("logo", file)}
                  />
                  <PendingAssetField
                    label="Logo compacto"
                    file={branding.compactLogo}
                    disabled={creating}
                    helperText="Versión cuadrada o reducida para navbar móvil."
                    onChange={(file) => handleBrandingChange("compactLogo", file)}
                  />
                  <PendingAssetField
                    label="Favicon"
                    file={branding.favicon}
                    disabled={creating}
                    accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                    helperText="Preferentemente cuadrado. SVG, PNG, WebP o ICO."
                    onChange={(file) => handleBrandingChange("favicon", file)}
                  />

                  {createStatus ? <Alert severity="info">{createStatus}</Alert> : null}
                  <Button type="submit" disabled={creating} variant="contained" size="large" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                    {creating
                      ? "Preparando proyecto..."
                      : superAdmin
                        ? "Crear proyecto"
                        : ownedCount === 0
                          ? "Crear mi primer proyecto"
                          : "Agregar proyecto"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={showCreatePanel ? 7 : 12}>
          <Stack spacing={2}>
            {loading && <Alert severity="info">Cargando proyectos...</Alert>}
            {projects.map((projectItem) => {
              const isActive = activeProject?.slug === projectItem.slug;
              const modules = normalizeProjectModules(projectItem.modules);
              const moduleLabels = activeModuleLabels(projectItem);
              return (
                <Card key={projectItem.id || projectItem.slug} elevation={0} sx={{ borderRadius: 4, border: `1px solid ${isActive ? BRAND.orange : "rgba(25,36,47,0.08)"}` }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                            {projectItem.name}
                          </Typography>
                          {isActive && <Chip label="Activo" size="small" sx={{ bgcolor: "rgba(243,90,58,0.12)", color: BRAND.orange, fontWeight: 900 }} />}
                        </Stack>
                        <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.75 }}>
                          /{projectItem.slug} · DB: {projectItem.dbName} · Plan: {BILLING_PLAN_LABELS[normalizeProjectPlanCode(projectItem.billing?.planCode || projectItem.plan)]}
                        </Typography>
                        {projectItem.domains?.length ? (
                          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)", mt: 0.45 }}>
                            Dominios asociados: {projectItem.domains.join(", ")}
                          </Typography>
                        ) : null}
                        <Box sx={{ mt: 1.5, maxWidth: 760 }}>
                          <ProjectDeploymentCard project={projectItem} compact />
                        </Box>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          <Chip size="small" label={`Billing: ${projectItem.billing?.status || "trial"}`} color={["active", "trial", "exempt"].includes(projectItem.billing?.status || "trial") ? "success" : "warning"} />
                          <Chip size="small" label={`Página: ${projectItem.status || "active"}`} variant="outlined" />
                        </Stack>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                          {moduleLabels.length > 0 ? moduleLabels.map((label) => (
                            <Chip key={label} size="small" label={label} variant="outlined" />
                          )) : <Chip size="small" label="Sin módulos" />}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant={isActive ? "contained" : "outlined"} onClick={() => setActiveProject(projectItem)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, bgcolor: isActive ? BRAND.orange : "transparent", borderColor: "rgba(25,36,47,0.18)" }}>
                          Usar
                        </Button>
                        {superAdmin && (
                          <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/general`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Administrar
                          </Button>
                        )}
                        <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/suscripcion`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.orange }}>
                          Plan
                        </Button>
                        {modules.landing && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pagina`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Landing
                          </Button>
                        )}
                        {modules.products && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/productos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Productos
                          </Button>
                        )}
                        {modules.orders && (
                          <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pedidos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                            Pedidos
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
